import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

export default function AdminUpdateUserProfile() {
  const { t } = useTranslation();

  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);

  const [profileInfo, setProfileInfo] = useState({
    sponsor: "",
    sponsor_no: "",
    notes: "",
  });

  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] =
      `Bearer ${localStorage.getItem("token")}`;

    fetchAllUsers();
  }, []);

  const clearProfileInfo = () => {
    setProfileInfo({
      sponsor: "",
      sponsor_no: "",
      notes: "",
    });
  };

  const fetchAllUsers = async () => {
    setLoadingUsers(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/users`
      );

      const users = Array.isArray(res.data) ? res.data : [];

      setAllUsers(users);

      return users;
    } catch (err) {
      console.error("❌ Failed to fetch users", err);

      setAllUsers([]);

      setMessage(
        "❌ " +
          t(
            "admin_update_user_profile.messages.fetch_users_failed",
            "Kullanıcılar alınamadı."
          )
      );

      return [];
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserProfile = async (userId, vendorId) => {
    if (!userId) {
      clearProfileInfo();
      return null;
    }

    setLoadingProfile(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/update-user-profile`,
        {
          params: {
            user_id: userId,
            vendor_id: vendorId,
          },
        }
      );

      const profile =
        res.data?.profile ||
        res.data?.vendor_profile ||
        res.data;

      setProfileInfo({
        sponsor: profile?.sponsor || "",
        sponsor_no: profile?.sponsor_no || "",
        notes: profile?.notes || "",
      });

      setSelectedVendorId(
        profile?.vendor_id ||
          vendorId ||
          null
      );

      return profile;
    } catch (err) {
      console.error("❌ Failed to fetch user profile", err);

      clearProfileInfo();

      setSelectedVendorId(vendorId || null);

      setMessage(
        "❌ " +
          (
            err.response?.data?.detail ||
            t(
              "admin_update_user_profile.messages.fetch_profile_failed",
              "Profil alınamadı."
            )
          )
      );

      return null;
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleUserChange = async (option) => {
    const user = option?.user || null;

    setMessage("");
    setLastAction("");

    if (!user) {
      setSelectedUserId(null);
      setSelectedVendorId(null);
      clearProfileInfo();
      return;
    }

    const userId = user.user_id;

    const vendorId =
      user.wallet_vendor_id ||
      user.vendor_id ||
      null;

    setSelectedUserId(userId);
    setSelectedVendorId(vendorId);

    /*
     * Do not populate the fields from the /admin/users response.
     *
     * The profile fields are loaded directly from the database
     * through GET /admin/update-user-profile.
     */
    clearProfileInfo();

    await fetchUserProfile(
      userId,
      vendorId
    );
  };

  const handleUpdateProfile = async () => {
    if (!selectedUserId || !selectedVendorId) {
      setMessage(
        "❌ " +
          t(
            "admin_update_user_profile.messages.missing_user",
            "Lütfen kullanıcı seçin."
          )
      );

      return;
    }

    setSubmitting(true);
    setMessage("");
    setLastAction("update");

    try {
      /*
       * profileInfo contains the user's LOCAL edits.
       *
       * Nothing is written to the database until this POST.
       */
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/update-user-profile`,
        {
          user_id: selectedUserId,
          vendor_id: selectedVendorId,
          sponsor: profileInfo.sponsor,
          sponsor_no: profileInfo.sponsor_no,
          notes: profileInfo.notes,
        }
      );

      /*
       * Reload the profile from the database after saving.
       *
       * The form therefore shows the actual committed database state,
       * rather than simply continuing to display the local React state.
       */
      await fetchUserProfile(
        selectedUserId,
        selectedVendorId
      );

      /*
       * Refresh the user list as well, in case information used
       * by the Select label has changed.
       */
      await fetchAllUsers();

      setMessage(
        "✅ " +
          t(
            "admin_update_user_profile.messages.success"
          )
      );
    } catch (err) {
      console.error(
        "❌ Failed to update user vendor profile",
        err
      );

      setMessage(
        "❌ " +
          (
            err.response?.data?.detail ||
            t(
              "admin_update_user_profile.messages.failure"
            )
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const userOptions = allUsers.map((user) => ({
    value: user.user_id,
    label:
      `${user.fullname || "-"} | ` +
      `${user.userno || "-"} | ` +
      `${user.sponsor || "-"}`,
    user,
  }));

  const selectedUserOption =
    userOptions.find(
      (option) => option.value === selectedUserId
    ) || null;

  return (
    <StandardScreen
      background="default"
      topNav={
        <TopNav
          title={t(
            "admin_update_user_profile.title"
          )}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div
          style={{
            maxWidth: 720,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                  }}
                >
                  {t(
                    "admin_update_user_profile.title"
                  )}
                </h1>

                <FormField
                  label={t(
                    "admin_update_user_profile.fields.select_user.label"
                  )}
                >
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isLoading={loadingUsers}
                    options={userOptions}
                    value={selectedUserOption}
                    onChange={handleUserChange}
                    placeholder={t(
                      "admin_update_user_profile.fields.select_user.placeholder"
                    )}
                  />
                </FormField>

                {selectedUserId && (
                  <>
                    <FormField
                      label={t(
                        "admin_update_user_profile.fields.sponsor.label"
                      )}
                    >
                      <TextInput
                        value={profileInfo.sponsor}
                        disabled={
                          loadingProfile ||
                          submitting
                        }
                        onChange={(e) =>
                          setProfileInfo(
                            (current) => ({
                              ...current,
                              sponsor:
                                e.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "admin_update_user_profile.fields.sponsor_no.label"
                      )}
                    >
                      <TextInput
                        value={
                          profileInfo.sponsor_no
                        }
                        disabled={
                          loadingProfile ||
                          submitting
                        }
                        onChange={(e) =>
                          setProfileInfo(
                            (current) => ({
                              ...current,
                              sponsor_no:
                                e.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "admin_update_user_profile.fields.notes.label"
                      )}
                    >
                      <TextInput
                        value={profileInfo.notes}
                        disabled={
                          loadingProfile ||
                          submitting
                        }
                        onChange={(e) =>
                          setProfileInfo(
                            (current) => ({
                              ...current,
                              notes:
                                e.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <Button
                      onClick={
                        handleUpdateProfile
                      }
                      disabled={
                        submitting ||
                        loadingProfile
                      }
                      fullWidth
                    >
                      {submitting
                        ? t(
                            "admin_update_user_profile.status.submitting",
                            "Güncelleniyor..."
                          )
                        : lastAction ===
                            "update" &&
                          message.startsWith(
                            "✅"
                          )
                        ? t(
                            "admin_update_user_profile.status.success_short"
                          )
                        : lastAction ===
                            "update" &&
                          message.startsWith(
                            "❌"
                          )
                        ? `${t(
                            "admin_update_user_profile.status.error_short"
                          )}: ${message.replace(
                            "❌ ",
                            ""
                          )}`
                        : t(
                            "admin_update_user_profile.actions.submit"
                          )}
                    </Button>
                  </>
                )}
              </Stack>
            </Card>

            <StatusMessage
              message={message}
            />
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}